/*
 * Agent search results page: resolves the location from the URL or keyword query,
 * geocodes it, and renders nearby agents on the listing and map.
 * Highlighted cards omit the separator that floated behind their top edges.
 * Under SSR the page declares its primary work and completes once the final agent list is committed:
 * a non-empty list, a finished empty search, or the settled zoom-out retry. Handler errors surface
 * as unhandled exceptions, which the renderer reports as retryable failures.
 * Location SEO belongs only to location templates; directory roots retain their own metadata.
 * Keywords that match no place fall back to the default location instead of failing the page.
 */
import { app, Component } from 'apprun';
import { paginateAssets, sortAgentsOrAgencies, PageSize, setSearchResultValues, getAgentLink, moveTo, $, showLoading, distance, hasSameItem, checkMapList } from './utility';
import { getGeoCode, geocodePlace, getAllAgents } from './model/agent';
import Agent from './template/search-agent-template';
import SearchAgentForm from './searchAgentForm';
import { lang } from './Txlat';
import { getLocationKeyword } from './location-path';
import { applyLocationSeo, buildLocationSeo, setLocationHeading } from './location-seo';
import PageControl from './pageControl';
import './osm-map.ts';
import InquiryForm from './inquiry-form';
import { beginSSRRender } from './ssr-render';

const filterAgents = function (agents, name, specialities) {
  if (name) {
    agents = agents.filter(p => p.name.replace(/[^a-zA-Z ]/g, ' ').replace(/\s+/g, ' ').toLowerCase().includes(name.replace(/[^a-zA-Z ]/g, ' ').replace(/\s+/g, ' ').toLowerCase()));
  }
  if (specialities) {
    agents = agents.filter(p => hasSameItem((lang.get() === 'fr' ? p.business_types_FR : p.business_types) ?? '', specialities ?? ''));
  }
  return agents;

};

declare const L;
function getMapAgents(agents) {
  const thumbnail = agent => `${agent.photo_path ? `<div class="item-header"><a target=agent${agent.id} href="${url(agent)}"><img class="img-fluid" style="width:100px; height:130px" src="${agent.photo_path}" alt="${agent.name}"/></a></div>` : ''}`;
  const url = agent => lang.get() === 'fr' ? getAgentLink(agent, 'fr') : getAgentLink(agent, 'en');

  const popup = (agent) => {
    return `<div class="item-wrap">
${thumbnail(agent)}
<div class="item-body flex-grow-1" style='height:130px;'>
<h2 class="item-title">
  <a target=agent${agent.id} href=${url(agent)}>${agent.name}</a>
</h2>
<ul class="list-unstyled item-info">
<li class="item-type">${((lang.get() === 'fr') ? agent.title_FR : agent.title)}</li>
<li class="item-type">${agent.officeName}</li>
<li class="item-type">${agent.city} ${agent.province} ${agent.postal_code}</li>
</ul>
</div>
</div>`;
  };

  return agents.map(agent => ({
    id: agent.id,
    title: agent.name,
    lat: agent.latitude,
    lng: agent.longitude,
    marker: '/wp-content/themes/houzez/img/map/pin-agent.png',
    retinaMarker: '/wp-content/themes/houzez/img/map/pin-agent.png',
    popup: popup(agent)
  }));
}

export default class agentSearchResult extends Component {
  private completeSSR = beginSSRRender();

  rendered = (state) => {
    // An empty nearby result is intermediate: the zoom-out may load a larger list.
    if (state.agents && (state.agents.length || state.agentResult !== 'agentnearby')) { this.completeSSR(); }
  };

  view = (state) => {
    if (state.loadError) {
      return <div class="alert alert-danger" role="alert">
        {lang.get() === 'fr' ? 'Impossible de charger les courtiers. Veuillez réessayer.' : 'Unable to load agents. Please try again.'}
        <button class="btn btn-outline-danger ml-2" onclick={() => app.run('#')}>{lang.get() === 'fr' ? 'Réessayer' : 'Retry'}</button>
      </div>;
    }
    if (!state.agents) { return; }
    if (document.querySelector('#agents-location-title')) {
      applyLocationSeo(document, buildLocationSeo(location.pathname, location.search, state.agentResult === 'agent' ? state.agents.length : 0));
    }

    app.render($('#resultFound'), <PageControl page={state.page} total={state.agents.length} size={PageSize} event='/gotoPage' type={state.agentResult} isMlsDeleted='' />);
    return <>
      <div>
        <div id="houzez_ajax_container">
          <div class="card-deck">
            {state.highlighedProp &&
              <div style='width:100%'><Agent agent={state.highlighedProp} hightlight={true} />
              </div>}
            {state.pageAgents.map(a => <Agent agent={a} hightlight={false} />)}
          </div>
        </div>
        {state.agents.length ? <PageControl page={state.page} total={state.agents.length} size={PageSize} event='/gotoPage' type='agent' isMlsDeleted='' /> : <></>}
        <p></p>
      </div>
    </>;
  };

  update = {
    '#': async (state) => {

      let keyword = getLocationKeyword(window.location.pathname);

      setLocationHeading(document, '#agents-location-title', window.location.pathname);
      checkMapList();

      const params = new URLSearchParams(location.search);
      const keywordParam = params.get('keyword')?.trim().replace('/', '') || '';
      if (keywordParam) {
        keyword = keywordParam;
      }
      if (keyword) {
        keyword = keyword.replace(/\s+/g, '');
        keyword = keyword.replace(/-/g, ' ');
      }
      const name = params.get('name') ? params.get('name').trim() : '';

      const sortBy = params.get('sortby') ? params.get('sortby') : '';

      setSearchResultValues(keyword, name, sortBy);

      const place = geocodePlace(await getGeoCode(keyword));
      const bounds = place.geometry.viewport;

      const locationInput = ($('[name="keyword"]') as HTMLInputElement);
      if (!location.search && !locationInput.value) {
        locationInput.value = place.formatted_address;
      }
      state.agentResult = 'agent';
      app.run('/map-set-boundary', bounds);

    },

    '/map-load-list': async (state, lowerBounds, upperBounds) => {
      showLoading('#resultFound');

      const name = ($('[name="name"]') as HTMLInputElement).value;

      const specialities = document.getElementsByName('specialty[]')[0] as HTMLSelectElement;

      let specialitiesStr;
      for (let i = 0; i < specialities.selectedOptions.length; i++) {
        if (specialities.selectedOptions[i].value) {
          if (specialitiesStr) {
            specialitiesStr += ',' + specialities.selectedOptions[i].value.toLowerCase();
          } else {
            specialitiesStr = specialities.selectedOptions[i].value.toLowerCase();
          }
        }
      }

      const location = ($('[name="keyword"]') as HTMLInputElement).value;

      try {
        state.all_agents = await getAllAgents();
      } catch (error) {
        if ((window as any).__RLP_SSR__) { throw error; }
        const summary = $('#resultFound');
        if (summary) { summary.textContent = ''; }
        return { ...state, loadError: true, agents: [], pageAgents: [] };
      }
      state.loadError = false;

      let filterdAgent = filterAgents(state.all_agents, name, specialitiesStr);
      let ignoreMapMove = true;

      if (location) {
        filterdAgent = filterdAgent.filter(p => p.latitude >= lowerBounds.lat && p.latitude <= upperBounds.lat && p.longitude >= lowerBounds.lng && p.longitude <= upperBounds.lng);
      } else {
        ignoreMapMove = false;
      }

      const centerLat = (lowerBounds.lat + upperBounds.lat) / 2;
      const centerLng = (lowerBounds.lng + upperBounds.lng) / 2;
      filterdAgent = filterdAgent.map(a => ({
        ...a,
        distance: distance(centerLat, centerLng, a.latitude, a.longitude),
      }));

      if (state.searchName === name && state.searchSpecialty === specialitiesStr) {
        ignoreMapMove = true;
      } else {
        state.searchName = name;
        state.searchSpecialty = specialitiesStr;
        state.lowerBounds = lowerBounds;
        state.upperBounds = upperBounds;
      }

      state.agents = sortAgentsOrAgencies(filterdAgent, state.sortBy);

      state.page = 1;
      state.pageAgents = paginateAssets(state.agents, state.page);

      state.unfilteredAgents = state.all_agents;

      if (filterdAgent.length === 0 && name === '') {
        state.agentResult = 'agentnearby';
        app.run('/map-zoomout', 8);
        // The retry reuses loaded data, so any resulting list commits before this task runs.
        setTimeout(() => this.completeSSR(), 0);
        return state;
      } else {
        app.run('map', getMapAgents(state.agents), ignoreMapMove);
      }

      return state;
    },

    '/map-refresh-list': async (state, bounds) => {
      if (!state.all_agents) { return; }

      showLoading('#resultFound');

      const agents = state.agents.filter(p => bounds.contains(L.latLng(p.latitude, p.longitude)));

      state.agents = agents;
      state.page = 1;
      state.agents = sortAgentsOrAgencies(state.agents, state.sortby);
      state.pageAgents = paginateAssets(state.agents, 1);
      state.unfilteredAgents = state.agents;

      return state;

    },

    '/sortAgentsOrAgencies': async (state) => {
      const s = $('#ajax_sort_properties') as HTMLInputElement;
      state.sortby = s.value;

      state.agents = sortAgentsOrAgencies(state.agents, s.value);

      state.page = 1;
      state.pageAgents = paginateAssets(state.agents, 1);

      return state;
    },

    '/highlight-card': (state, map_agent) => {
      moveTo('.card:first-of-type');
      let highlighedProp = '';
      if (map_agent) {
        highlighedProp = state.all_agents.find(p => p.id === map_agent.id);
      }
      return { ...state, highlighedProp };
    },

    '/gotoPage': (state, targetPageNumber) => {
      state.page = targetPageNumber;
      state.pageAgents = paginateAssets(state.agents, targetPageNumber);
      return { ...state };
    },

    'close-highlighed-card': (state) => {
      document.querySelector('form').scrollIntoView();
      state.highlighedProp = null;
      return state;
    },

    '/getSelectedAgent': (state, agent) => {
      state.selectedAgent = agent;
      return state;
    },

    '/set_map_view': async (state, v) => {

      const url = new URL(window.location.href);
      const last_map_view = url.searchParams.get('map_view');

      if (v !== last_map_view) {
        url.searchParams.set('map_view', v);
        window.history.pushState({}, '', url.href);
      }

      return state;
    },

    '/clearError': (state, elementName) => {
      const inputName = document.getElementById(elementName);
      const inputNametxt = document.getElementById(elementName + 'txt');
      if (inputName) { inputName.classList.remove('is-valid'); }
      if (inputName) { inputName.classList.remove('is-invalid'); }
      if (inputNametxt) { inputNametxt.style.display = 'none'; }
    },

    // '/submitInquery': (state, e) => {
    //   const validated = validateAgentInqueryForm();
    // },
  };

}

new agentSearchResult().start('.listing-view');
new SearchAgentForm().start('.advanced-search.advanced-search-half-map');
new InquiryForm({ isPopup: true }).start('.mobile-property-form .modal-body');

window.addEventListener('resize', checkMapList);
